import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Filter, Search, ChevronDown, ChevronUp, Star } from 'lucide-react';
import ProductCard from '@/components/product/ProductCard';
import ProductCardSkeleton from '@/components/product/ProductCardSkeleton';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import { categoriesApi, productsApi } from '@/services/api';
import { debounce } from '@/utils/utils';
import { useCart } from '@/store/cartStore';
import { toast } from 'react-hot-toast';

const ProductsPage: React.FC = () => {
    //const navigate = useNavigate();
    //const location = useLocation();
    const { addProduct } = useCart();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [listaCategorias, setListaCategorias] = useState<any>([]);
    const getCategorias = async () => {
        try {
            const response = await categoriesApi.getCategories({});
            setListaCategorias(response);
            //console.log("cat", response);
        } catch (error) {
            console.error('Error al obtener las categorías:', error);
        }
    }
    useEffect(() => {
        getCategorias();
    }, []);
    const [filters, setFilters] = useState<any>({
        /*search: '',
        category: '',
        //sort: 'popular',
        sort: 'rating_desc',//'name_asc' | 'name_desc' | 'price_asc' | 'price_desc' | 'created_desc' | 'created_asc' | 'rating_desc'
        minPrice: '',
        maxPrice: ''*/
        search: searchParams.get('q') || '',
        category: searchParams.get('category') || '',
        sort: searchParams.get('sort') || 'popular',
        minPrice: searchParams.get('min_price') || '',
        maxPrice: searchParams.get('max_price') || ''
    });

    const [searchInput, setSearchInput] = useState('')
    // Sincronizar el input con el estado cuando se presiona Enter
    /*const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchInput.trim().length >= 3 || searchInput.trim() === '') {
            setFilters({ ...filters, search: searchInput.trim() });
        } else {
            toast.error('Ingresa al menos 3 caracteres para buscar');
        }
    };*/
    const [showMinLengthError, setShowMinLengthError] = useState(false);

    // 1. Crear la función de búsqueda debounceada usando useCallback
    const debouncedSearch = useCallback(
        debounce((searchValue: string) => {
            if (searchValue.trim().length >= 3 || searchValue.trim() === '') {
                setFilters((prev: any) => ({ ...prev, search: searchValue.trim() }));
                setShowMinLengthError(false);
            } else {
                setShowMinLengthError(true);
            }
        }, 1200), // 500ms de delay
        [] // Dependencias vacías porque debounce ya está memoizada
    );

    // 2. Efecto que ejecuta la búsqueda debounceada cuando searchInput cambia
    useEffect(() => {
        debouncedSearch(searchInput);

        // Limpiar el error si el input está vacío
        if (searchInput === '') {
            setShowMinLengthError(false);
        }

        // Limpieza al desmontar
        return () => {
            debouncedSearch.cancel(); // Necesitarás implementar cancel en tu debounce
        };
    }, [searchInput, debouncedSearch]);

    // 3. Función para manejar el submit manual (Enter)
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchInput.trim().length >= 3 || searchInput.trim() === '') {
            setFilters((prev: any) => ({ ...prev, search: searchInput.trim() }));
            setShowMinLengthError(false);
        } else {
            setShowMinLengthError(true);
        }
    };

    useEffect(() => {
        if (searchInput === '') {
            setFilters({ ...filters, search: '' });
        }
    }, [searchInput]);
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.search) params.set('q', filters.search);
        if (filters.category) params.set('category', filters.category);
        if (filters.sort !== 'popular') params.set('sort', filters.sort);
        if (filters.minPrice) params.set('min_price', filters.minPrice);
        if (filters.maxPrice) params.set('max_price', filters.maxPrice);
        if (filters.category_id) params.set('category_id', filters.category.toString());
        console.log('Filters:', filters, params.entries());
        setSearchParams(params);
    }, [filters, setSearchParams]);

    // Consulta para productos
    const { data: products, isLoading, isError } = useQuery({
        queryKey: ['products', filters],
        queryFn: () => productsApi.getProducts({
            search: filters.search,
            category_id: filters.category,
            sort: filters.sort,
            min_price: filters.minPrice,
            max_price: filters.maxPrice,
            page: 1,
            per_page: 24
        }),
        staleTime: 5 * 60 * 1000
    });

    /*const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(`/products?q=${encodeURIComponent(filters.search)}`);
    };*/

    const handleAddToCart = async (productId: number) => {
        try {
            await addProduct(productId, 1);
            toast.success('Producto agregado al carrito');
        } catch (error) {
            toast.error('Error al agregar al carrito');
        }
    };

    if (isError) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <div className="bg-red-50 text-red-600 p-4 rounded-lg inline-block">
                    Error al cargar los productos. Intenta nuevamente.
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen">
            {/* Hero Section para Products */}
            <div className="bg-primary-600 text-white py-8">
                <div className="container mx-auto px-4">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2">Explora Nuestros Productos</h1>
                    <p className="text-primary-100">Encuentra lo que necesitas al mejor precio</p>
                </div>
            </div>

            {/* Filtros y ordenación */}
            <div className="container mx-auto px-4 py-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Buscador */}
                        <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-2xl">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            {/* <Input
                                type="text"
                                placeholder="Buscar productos..."
                                className="pl-10 pr-4 py-2 w-full"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            /> */}
                            <input
                                type="text"
                                placeholder="Filtrar por nombre..."
                                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearchSubmit(e);
                                    }
                                }}
                            />
                            {showMinLengthError && (
                                <div className="absolute left-0 right-0 -bottom-6 text-xs text-red-500">
                                    Ingresa al menos 3 caracteres
                                </div>
                            )}
                        </form>

                        {/* Botón Filtros móvil */}
                        <Button
                            variant="outline"
                            className="md:hidden flex items-center gap-2"
                            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                        >
                            <Filter className="w-4 h-4" />
                            Filtros
                            {isFiltersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>

                        {/* Ordenación desktop */}
                        <div className="hidden md:block">
                            <Select
                                //label="Ordenar por:"
                                placeholder='Ordenar Productos por:'
                                value={filters.sort}
                                //onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                                onChange={(value) => setFilters({ ...filters, sort: value as 'rating_desc' | 'price_asc' | 'price_desc' | 'newest' | 'rating' })}
                                options={[
                                    { value: 'rating_desc', label: 'Más populares' },
                                    { value: 'price_asc', label: 'Precio: menor a mayor' },
                                    { value: 'price_desc', label: 'Precio: mayor a menor' },
                                    { value: 'newest', label: 'Más recientes' },
                                    { value: 'rating', label: 'Mejor valorados' }
                                ]}
                            />
                        </div>
                    </div>

                    {/* Panel de filtros móvil */}
                    {isFiltersOpen && (
                        <div className="mt-4 md:hidden p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-medium mb-3 flex items-center gap-2">
                                <Filter className="w-4 h-4" />
                                Filtros
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <Select
                                    label="Categoría"
                                    value={filters.category}
                                    //onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                                    onChange={(value) => setFilters(value as any)}
                                    options={[
                                        { value: '', label: 'Todas' },
                                        { value: 'electronics', label: 'Electrónica' },
                                        { value: 'clothing', label: 'Ropa' },
                                        { value: 'home', label: 'Hogar' },
                                        { value: 'beauty', label: 'Belleza' }
                                    ]}
                                />
                                <Select
                                    label="Ordenar por"
                                    value={filters.sort}
                                    //onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                                    onChange={(value) => setFilters(value as 'rating_desc' | 'price_asc' | 'price_desc' | 'newest' | 'rating')}
                                    options={[
                                        { value: 'popular', label: 'Popularidad' },
                                        { value: 'price_asc', label: 'Precio ↑' },
                                        { value: 'price_desc', label: 'Precio ↓' },
                                        { value: 'newest', label: 'Nuevos' }
                                    ]}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Contenido principal */}
                <div className="flex flex-col md:flex-row gap-6">
                    {/* Sidebar de filtros (desktop) */}
                    <div className="hidden md:block w-64 flex-shrink-0">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-4">
                            <h3 className="font-medium mb-4">Filtrar por</h3>

                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-sm font-medium mb-2">Categorías</h4>
                                    <Select
                                        value={filters.category}
                                        //onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                                        onChange={(value) => setFilters({ ...filters, category: value as any })}
                                        /*options={[
                                            { value: '', label: 'Todas las categorías' },
                                            { value: 'electronics', label: 'Electrónicos' },
                                            { value: 'clothing', label: 'Ropa y accesorios' },
                                            { value: 'home', label: 'Hogar y jardín' },
                                            { value: 'beauty', label: 'Belleza y cuidado personal' }
                                        ]}*/
                                        options={listaCategorias.map((categoria: any) => ({
                                            value: categoria.id,
                                            label: categoria.name
                                        }))}
                                    />
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium mb-2">Rango de precios</h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            type="number"
                                            placeholder="Mínimo"
                                            value={filters.minPrice}
                                            onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Máximo"
                                            value={filters.maxPrice}
                                            onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium mb-2">Valoración</h4>
                                    <div className="space-y-2">
                                        {[4, 3, 2].map((stars) => (
                                            <label key={stars} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="rounded text-primary-600"
                                                />
                                                <div className="flex">
                                                    {[...Array(5)].map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            className={`w-4 h-4 ${i < stars ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-sm text-gray-600">y más</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lista de productos */}
                    <div className="flex-1">
                        {/* Encabezado y ordenación móvil */}
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="font-medium text-gray-900">
                                {products?.meta?.total || '0'} productos encontrados
                            </h2>
                            <div className="md:hidden">
                                <Select
                                    value={filters.sort}
                                    //onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                                    onChange={(value) => setFilters(value as 'rating_desc' | 'price_asc' | 'price_desc' | 'newest' | 'rating')}
                                    options={[
                                        { value: 'popular', label: 'Popular' },
                                        { value: 'price_asc', label: 'Precio ↑' },
                                        { value: 'price_desc', label: 'Precio ↓' }
                                    ]}
                                    size="sm"
                                />
                            </div>
                        </div>

                        {/* Lista de productos */}
                        {isLoading ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {[...Array(12)].map((_, i) => (
                                    <ProductCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : (
                            <>
                                {products?.data?.length ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {products.data.map((product) => (
                                            <ProductCard
                                                key={product.id}
                                                product={product}
                                                onAddToCart={() => handleAddToCart(product.id)}
                                                showQuickAdd
                                                imageSize="md"
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                                        <div className="text-gray-400 mb-4">
                                            <Search className="w-12 h-12 mx-auto" />
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                            No se encontraron productos
                                        </h3>
                                        <p className="text-gray-600 mb-4">
                                            Intenta ajustando tus filtros de búsqueda
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setFilters({
                                                    search: '',
                                                    category: '',
                                                    sort: 'popular',
                                                    minPrice: '',
                                                    maxPrice: ''
                                                });
                                            }}
                                        >
                                            Limpiar filtros
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Paginación */}
                        {products && products?.meta?.last_page > 1 && (
                            <div className="flex justify-center mt-8">
                                <div className="flex gap-2">
                                    <Button variant="outline" disabled={products?.meta.current_page === 1}>
                                        Anterior
                                    </Button>
                                    {[...Array(Math.min(5, products?.meta.last_page))].map((_, i) => (
                                        <Button
                                            key={i}
                                            variant={products?.meta.current_page === i + 1 ? 'solid' : 'outline'}
                                        >
                                            {i + 1}
                                        </Button>
                                    ))}
                                    <Button
                                        variant="outline"
                                        disabled={products.meta.current_page === products.meta.last_page}
                                    >
                                        Siguiente
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;